import {useFormContext} from 'react-hook-form';
import {useState} from 'react';
import {SVG} from '../../../../../../constants/assetLists';
import {NetworkDetailsFormValues, SelectedTreeNode} from '../../types';
import {
  canDeleteNodeWithoutContentGuard,
  getShowRemoveButton,
} from '../../hooks/useNetworkTreeGuards';
import {useSnackbar} from '../../../../../../context/SnackbarContextProvider';
import {canDeleteNode} from '../networkReferenceUtils';
import TreeDeleteConfirmationDialog from './TreeDeleteConfirmationDialog';
import { nodeSetupCompleteSchema } from '../../networkDetailsSchema';
import {isAdmin} from '../../../../../../utils';

interface SubBranchTreeRowProps {
  networkIndex: number;
  branchIndex: number;
  subBranchIndex: number;
  totalNetworkCount: number;
  networkBranchCount: number;
  onRemove: () => void;
  selectedNode: SelectedTreeNode | null;
  onSelectNode: (node: SelectedTreeNode) => void;
}

function SubBranchTreeRow({
  networkIndex,
  branchIndex,
  subBranchIndex,
  totalNetworkCount,
  networkBranchCount,
  onRemove,
  selectedNode,
  onSelectNode,
}: SubBranchTreeRowProps) {
  const {showSnackbar} = useSnackbar();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {watch} = useFormContext<NetworkDetailsFormValues>();

  const node = watch(
    `network.${networkIndex}.branches.${branchIndex}.subBranches.${subBranchIndex}`,
  );

  if (!node) return null;

  const validationResult = nodeSetupCompleteSchema.safeParse(node);
  const isNodeValid = validationResult.success;

  const depth = 2;
  const paddingInline = Math.min(12 + depth * 8, 56);

  const showRemoveButton = getShowRemoveButton({
    isNetwork: false,
    isBranch: false,
    totalNetworkCount,
    networkBranchCount,
  });

  const isSelected =
    selectedNode?.level === 'subBranch' &&
    selectedNode.networkIndex === networkIndex &&
    selectedNode.branchIndex === branchIndex &&
    selectedNode.subBranchIndex === subBranchIndex;

  const handleRemoveNode = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleConfirmRemove = () => {
    if (isAdmin() || canDeleteNodeWithoutContentGuard(node.id)) {
      onRemove();
      setShowDeleteDialog(false);
      return;
    }

    const result = canDeleteNode(node);

    if (!result.allowed) {
      showSnackbar({
        message: result.message,
        type: 'error',
        duration: 5000,
      });
      setShowDeleteDialog(false);
      return;
    }

    onRemove();
    setShowDeleteDialog(false);
  };

  return (
    <div key={networkIndex}>
      <TreeDeleteConfirmationDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleConfirmRemove}
      />
      <div
        role="button"
        tabIndex={0}
        className={`w-full flex justify-between items-center gap-2 py-2 rounded-lg transition-colors duration-200 hover:bg-pastelBlue group cursor-pointer ${
          isSelected ? 'bg-pastelBlue' : ''
        }`}
        style={{
          paddingInlineStart: `${paddingInline}px`,
          paddingInlineEnd: '8px',
        }}
        onClick={() =>
          onSelectNode({
            level: 'subBranch',
            networkIndex,
            branchIndex,
            subBranchIndex,
          })
        }
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            onSelectNode({
              level: 'subBranch',
              networkIndex,
              branchIndex,
              subBranchIndex,
            });
          }
        }}>
        <div className="flex items-center gap-2">
          <div className="w-[12px] h-[12px]" />
          <div className="w-[20px] h-[20px]">
            <img
              src={SVG.taskIcon}
              alt="icon"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`${!isNodeValid ? 'text-raspberryRed' : 'text-black'}`}>
              {node?.name || 'תת סניף'}
            </span>
            {!isNodeValid && (
              <span
                className="w-[14px] h-[14px] rounded-full bg-raspberryRed shrink-0"
                aria-label="שדה חובה חסר"
              />
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {showRemoveButton && (
            <button
              type="button"
              className="w-3 h-3 hidden group-hover:block"
              onClick={handleRemoveNode}>
              <img
                src={SVG.closeDarkIcon}
                className="w-full h-full object-contain"
                alt="remove"
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default SubBranchTreeRow;
